import React from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import {
  useAppSelector,
  useAppDispatch,
} from "../../../../../../common/hooks/hooks";
import { useGetBrandsQuery } from "../../../api/productTaxonomyApi";
import { toggleBrand } from "../../../productSlice";

const BrandFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // --- Data Fetching & State Management ---
  const {
    data: brandsData,
    isLoading: isBrandsLoading,
    isError: isBrandsError,
  } = useGetBrandsQuery();

  const isComponentLoading = isBrandsLoading || !areTranslationsReady;
  const selectedBrands = useAppSelector((state) => state.product.filters.brand);

  // Memoized selector to get the top 8 most used brands, sorted alphabetically.
  const topBrands = React.useMemo(() => {
    if (!brandsData?.brands) return [];

    // 1. Get all brands, 2. Filter active ones, 3. Sort by usage count (desc)
    const sortedByCount = Object.values(brandsData.brands)
      .filter((brand) => brand.count > 0)
      .sort((a, b) => b.count - a.count);

    // 4. Take the top 8, and 5. Sort them alphabetically for display
    return sortedByCount
      .slice(0, 8)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [brandsData]);

  const handleBrandClick = (brandSlug: string) => {
    dispatch(toggleBrand(brandSlug));
  };

  // If loading is finished, there's no error, and there are no brands to show, render nothing.
  if (!isComponentLoading && !isBrandsError && topBrands.length === 0) {
    return null;
  }

  return (
    <div className="filter-widget">
      <h3 className="filter-widget__title">
        {isComponentLoading ? (
          <Skeleton width={120} />
        ) : (
          t("productsArchive.sidebar.brandTitle")
        )}
      </h3>

      {/* State 1: Loading Skeleton */}
      {isComponentLoading && (
        <div className="filter-widget__pill-container">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              width={Math.random() * (100 - 60) + 60}
              height={28}
              borderRadius="999px"
            />
          ))}
        </div>
      )}

      {/* State 2: isFetching state is intentionally ignored to not cause UI flicker during background refetches,
       since taxonomy data rarely change. */}

      {/* State 3: API Error (will only show if there's an error and not loading) */}
      {!isComponentLoading && isBrandsError && (
        <p>{t("productsArchive.sidebar.brandError")}</p>
      )}

      {/* State 4: (No Brands Found) is handled by returning null above */}

      {/* State 5: Success with Brands (will only show if not loading, no error, and topBrands has items) */}
      {!isComponentLoading && !isBrandsError && topBrands.length > 0 && (
        <div className="filter-widget__pill-container">
          {topBrands.map((brand) => {
            const isSelected = selectedBrands.includes(brand.slug);
            return (
              <button
                key={brand.id}
                className={`filter-widget__pill ${
                  isSelected ? "is-active" : ""
                }`}
                onClick={() => handleBrandClick(brand.slug)}
                aria-pressed={isSelected}
              >
                {brand.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BrandFilter;
